// client/src/coms/views/welcome/WelcomeSections.js
// Presentational pieces of the Voxemera welcome screen (VoxemeraHomeView owns the data and actions). The layout
// follows the approved reference (2026-11-01): a plain "Voxemera" heading, four restrained cyan action buttons, a
// public-server panel with a create-your-own column, "Continue where you left off" and a footer under the column.
// No decorative artwork: server cards show the server's own banner or a colour that stays the same per server.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { initialsOf } from './welcomeData';
import { ServerBannerArt } from '../../components/ServerBanner';

// ---------- icons (inline, 24px grid, currentColor) ----------
const svg = (children, size = 24, sw = 1.9) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {children}
  </svg>
);
export const Icon = {
  pencil: (s) => svg(<><path d="M14.5 5.5 18.5 9.5" /><path d="M4.5 19.5l1-4.2L16 4.8a2.1 2.1 0 0 1 3 0l.2.2a2.1 2.1 0 0 1 0 3L8.7 18.5Z" /><path d="M4 21.5h8" /></>, s),
  sliders: (s) => svg(<><path d="M6 3.5v4M6 12.5v8M12 3.5v9M12 17.5v3M18 3.5v2.5M18 11v9.5" /><circle cx="6" cy="10" r="2.4" /><circle cx="12" cy="15" r="2.4" /><circle cx="18" cy="8.5" r="2.4" /></>, s),
  users: (s) => svg(<><circle cx="9" cy="8" r="3.4" /><path d="M2.8 19.5a6.2 6.2 0 0 1 12.4 0" /><path d="M15.8 4.9a3.4 3.4 0 0 1 0 6.3M18.2 13.6a6 6 0 0 1 3.2 5.4" /></>, s),
  link: (s) => svg(<><path d="M10 14a4.2 4.2 0 0 0 6 0l3.2-3.2a4.2 4.2 0 0 0-6-6L11.6 6.4" /><path d="M14 10a4.2 4.2 0 0 0-6 0l-3.2 3.2a4.2 4.2 0 0 0 6 6l1.6-1.6" /></>, s),
  play: (s) => svg(<><rect x="3" y="5" width="18" height="13" rx="2.5" /><path d="m10.3 8.8 4.6 2.7-4.6 2.7Z" /></>, s),
  chat: (s) => svg(<><path d="M20.5 11.5a8.3 8.3 0 0 1-12.2 7.3L3.5 20l1.3-4.3a8.3 8.3 0 1 1 15.7-4.2Z" /><path d="M8.6 11.6h.01M12.2 11.6h.01M15.8 11.6h.01" strokeWidth="2.6" /></>, s),
  servers: (s) => svg(<><rect x="4" y="3.5" width="16" height="7.5" rx="2.6" /><rect x="4" y="13" width="16" height="7.5" rx="2.6" /></>, s),
  home: (s) => svg(<><path d="M3.5 10.5 12 3.5l8.5 7" /><path d="M5.5 9v11h4.5v-6h4v6h4.5V9" fill="currentColor" fillOpacity="0.95" stroke="none" /></>, s),
  compass: (s) => svg(<><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2 5-5 2 2-5Z" /></>, s),
  download: (s) => svg(<><path d="M12 3.5v11.5M7 10.5l5 5 5-5" /><path d="M4.5 20.5h15" /></>, s),
  windows: (s) => svg(<path d="M3 5.4 10.4 4.4v7.1H3ZM11.6 4.2 21 3v8.5h-9.4ZM3 12.6h7.4v7.1L3 18.6ZM11.6 12.6H21V21l-9.4-1.3Z" fill="currentColor" stroke="none" />, s),
  globe: (s) => svg(<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3Z" /></>, s),
  phone: (s) => svg(<><rect x="6.5" y="2.5" width="11" height="19" rx="2.6" /><path d="M10.8 18.4h2.4" /></>, s),
  chevron: (s) => svg(<path d="m9 5.5 6.5 6.5L9 18.5" />, s, 2.2),
  chevronDown: (s) => svg(<path d="m5.5 9 6.5 6.5L18.5 9" />, s, 2.2),
  close: (s) => svg(<path d="M6 6l12 12M18 6 6 18" />, s, 2.2),
  apple: (s) => svg(<><path d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5a4.3 4.3 0 0 0-3.4-1.8c-1.4-.1-2.8.9-3.5.9s-1.8-.8-3-.8a4.5 4.5 0 0 0-3.8 2.3c-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7 2-1.1 2.8-2.3a9.6 9.6 0 0 0 1.3-2.6 4 4 0 0 1-2.5-3.8Z" fill="currentColor" stroke="none" /><path d="M14.2 5.6a4 4 0 0 0 .9-2.9 4.1 4.1 0 0 0-2.7 1.4 3.8 3.8 0 0 0-.9 2.8 3.4 3.4 0 0 0 2.7-1.3Z" fill="currentColor" stroke="none" /></>, s),
  linux: (s) => svg(<><path d="M12 2.8c-2.2 0-3.3 1.8-3.3 4.2 0 1.6-.3 2.7-1.4 4.3-1.2 1.7-2.3 3.6-2.1 5.6" /><path d="M12 2.8c2.2 0 3.3 1.8 3.3 4.2 0 1.6.3 2.7 1.4 4.3 1.2 1.7 2.3 3.6 2.1 5.6" /><path d="M5.2 17.4c-.9.4-1.6 1-1.4 1.8.3 1 2.2.9 3.7 1.6 1.3.6 2.3.4 2.6-.8M18.8 17.4c.9.4 1.6 1 1.4 1.8-.3 1-2.2.9-3.7 1.6-1.3.6-2.3.4-2.6-.8M10.1 20h3.8" /><path d="M10.6 7.2h.01M13.4 7.2h.01" strokeWidth="2.4" /><path d="M10.5 9.3 12 10.4l1.5-1.1" /></>, s, 1.6),
  arrow: (s) => svg(<path d="M4.5 12h15m-6-6 6 6-6 6" />, s, 2.2),
  people: (s) => svg(<><circle cx="9" cy="7.6" r="3.3" fill="currentColor" stroke="none" /><path d="M2.5 19.5a6.5 6.5 0 0 1 13 0Z" fill="currentColor" stroke="none" /><circle cx="16.6" cy="8.6" r="2.6" fill="currentColor" fillOpacity="0.75" stroke="none" /><path d="M15.8 13.2a5.4 5.4 0 0 1 5.9 6.3h-4.5a8 8 0 0 0-1.4-6.3Z" fill="currentColor" fillOpacity="0.75" stroke="none" /></>, s),
  search: (s) => svg(<><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4.2-4.2" /></>, s),
  refresh: (s) => svg(<path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7" />, s),
  lock: (s) => svg(<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>, s),
  plus: (s) => svg(<path d="M12 5v14M5 12h14" />, s, 2.2),
  clock: (s) => svg(<><circle cx="12" cy="12" r="8.6" /><path d="M12 7.4V12l3.2 2.2" /></>, s, 2),
};

// ---------- categories (labels for the "View all" filter) ----------
export const CATEGORY = {
  creative: { label: 'Creative' },
  gaming: { label: 'Gaming' },
  music: { label: 'Music' },
  tabletop: { label: 'Tabletop' },
  social: { label: 'Social' },
  education: { label: 'Education' },
  tech: { label: 'Tech' },
  science: { label: 'Science' },
  sports: { label: 'Sports' },
  other: { label: 'Other' },
};
export const categoryLabel = (c) => (CATEGORY[c] ? CATEGORY[c].label : '');

function hueOf(text) {
  let h = 0;
  for (const ch of String(text || '')) h = (h * 31 + ch.codePointAt(0)) % 360;
  return h;
}

export function ServerAvatar({ name, src, size = 48, className = '' }) {
  const [failed, setFailed] = useState(false);
  const showImg = !!src && !failed;
  return (
    <span className={`vxw-avatar ${className}`} style={{ width: size, height: size, '--vxw-hue': hueOf(name) }} aria-hidden="true">
      {showImg ? <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} /> : <span>{initialsOf(name)}</span>}
    </span>
  );
}

// ---------- header pieces ----------
// The contextual app-download action (smart app promotion). One primary action for this device, plus a small
// menu with every other real option; nothing at all inside the desktop app, a mobile wrapper or an installed web
// app (AppGet then receives promo.primary = null and no others).
function PromoAction({ option, className, busy, onSelect, children, title }) {
  if (option.url) {
    return (
      <a
        className={className}
        href={option.url}
        {...(option.external ? { target: '_blank', rel: 'noopener noreferrer' } : { download: option.file || '', rel: 'noopener' })}
        onClick={() => onSelect(option, { followed: true })}
        title={title}
        data-vxw-promo={option.id}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={className} onClick={() => onSelect(option)} disabled={busy === option.id} title={title} data-vxw-promo={option.id}>
      {children}
    </button>
  );
}

function AppGetMenu({ others, onSelect, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-vxw-appget-more]'))) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose(true);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc, { passive: true });
    document.addEventListener('keydown', onKey);
    const first = ref.current && ref.current.querySelector('[role="menuitem"]');
    if (first) first.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const iconFor = (o) => (Icon[o.icon] || Icon.download)(20);
  return (
    <div className="vxw-menu vxw-menu-right vxw-appget-menu" ref={ref} role="menu" aria-label="Other ways to get Voxemera">
      <div className="vxw-menu-title">Get Voxemera</div>
      {others.map((o) =>
        o.url ? (
          <a
            key={o.id}
            role="menuitem"
            className="vxw-menu-item"
            href={o.url}
            {...(o.external ? { target: '_blank', rel: 'noopener noreferrer' } : { download: o.file || '', rel: 'noopener' })}
            onClick={() => {
              onSelect(o, { followed: true });
              onClose();
            }}
          >
            <span className={`vxw-appget-ico kind-${o.kind}`}>{iconFor(o)}</span>
            <span className="vxw-appget-item">
              <span className="vxw-menu-name">{o.menu}</span>
              <span className="vxw-appget-sub">{o.sub}</span>
            </span>
          </a>
        ) : (
          <button
            key={o.id}
            type="button"
            role="menuitem"
            className="vxw-menu-item"
            onClick={() => {
              onClose();
              onSelect(o);
            }}
          >
            <span className={`vxw-appget-ico kind-${o.kind}`}>{iconFor(o)}</span>
            <span className="vxw-appget-item">
              <span className="vxw-menu-name">{o.menu}</span>
              <span className="vxw-appget-sub">{o.sub}</span>
            </span>
          </button>
        )
      )}
      <div className="vxw-appget-note">Everything also works right here in your browser, with nothing to install.</div>
    </div>
  );
}

export function AppGet({ promo, busy, onSelect }) {
  const [open, setOpen] = useState(false);
  const moreRef = useRef(null);
  const close = useCallback((refocus) => {
    setOpen(false);
    if (refocus && moreRef.current) moreRef.current.focus({ preventScroll: true });
  }, []);
  if (!promo) return null;
  const { primary, others, style } = promo;
  if (!primary && !others.length) return null;

  const label = (text, short) =>
    short ? (
      <>
        <span className="vxw-label-full">{text}</span>
        <span className="vxw-label-short">{short}</span>
      </>
    ) : (
      <span className="vxw-label-full">{text}</span>
    );

  // One bordered control, like the approved "Get the app ▾": the contextual action on the left and, when other
  // platforms exist, a chevron that opens them. Without a contextual action the whole control opens the menu.
  return (
    <div className="vxw-appget" data-primary={primary ? primary.id : ''} data-style={style}>
      <div className={`vxw-getapp ${primary && others.length ? 'is-split' : ''}`} role="group" aria-label="Get the Voxemera app">
        {primary ? (
          <PromoAction option={primary} className="vxw-getapp-main" busy={busy} onSelect={onSelect} title={primary.sub}>
            {Icon.download(18)}
            {label(primary.label, primary.short)}
          </PromoAction>
        ) : null}
        {others.length ? (
          <button
            type="button"
            ref={moreRef}
            className={primary ? 'vxw-getapp-more' : 'vxw-getapp-main vxw-getapp-only'}
            data-vxw-appget-more
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={primary ? 'More ways to get Voxemera' : 'Get the Voxemera app'}
            title={primary ? 'Other platforms' : undefined}
            onClick={() => setOpen((v) => !v)}
          >
            {primary ? null : Icon.download(18)}
            {primary ? null : label('Get the app')}
            {Icon.chevronDown(16)}
          </button>
        ) : null}
      </div>
      {open ? <AppGetMenu others={others} onSelect={onSelect} onClose={close} /> : null}
    </div>
  );
}

// The one optional install suggestion (see shouldShowNudge in welcomePlatform.js). Not modal, never takes focus.
export function AppNudge({ option, onSelect, onDismiss }) {
  if (!option) return null;
  const what =
    option.kind === 'native'
      ? `${option.sub || 'The Voxemera app'} is ready to download.`
      : option.id === 'ios-home' || option.id === 'mac-dock'
        ? 'You can keep Voxemera one tap away as a web app.'
        : 'You can install Voxemera as a web app with its own window.';
  return (
    <aside className="vxw-nudge" aria-label="Get the Voxemera app">
      <span className="vxw-nudge-icon">{(Icon[option.icon] || Icon.download)(20)}</span>
      <div className="vxw-nudge-text">
        <strong>Prefer an app?</strong>
        <span>{what} Or keep using it right here in your browser.</span>
      </div>
      <div className="vxw-nudge-actions">
        <PromoAction option={option} className="vxw-nudge-go" onSelect={onSelect} title={option.sub}>
          {option.label}
        </PromoAction>
        <button type="button" className="vxw-nudge-later" onClick={onDismiss}>
          Not now
        </button>
      </div>
      <button type="button" className="vxw-nudge-x" onClick={onDismiss} aria-label="Dismiss">
        {Icon.close(16)}
      </button>
    </aside>
  );
}

// "Communities": the account's servers (and Create), or sign in.
export function CommunitiesMenu({ open, onClose, servers, logos, loaded, onOpenServer, onCreateServer }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-vxw-communities]'))) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="vxw-menu" ref={ref} role="menu" aria-label="Your communities">
      <div className="vxw-menu-title">Your communities</div>
      {!loaded ? <div className="vxw-menu-empty">Loading…</div> : null}
      {loaded && !servers.length ? <div className="vxw-menu-empty">You haven’t joined a server yet.</div> : null}
      {servers.slice(0, 14).map((s) => (
        <button key={s.id} type="button" role="menuitem" className="vxw-menu-item" onClick={() => onOpenServer(s)}>
          <ServerAvatar name={s.name} src={logos[String(s.id)]} size={30} />
          <span className="vxw-menu-name">{s.name}</span>
          {Number(s.unread_count) > 0 ? <span className="vxw-dot" aria-label="Unread messages" /> : null}
        </button>
      ))}
      <button type="button" role="menuitem" className="vxw-menu-item is-create" onClick={onCreateServer}>
        <span className="vxw-avatar vxw-avatar-add" style={{ width: 30, height: 30 }} aria-hidden="true">
          {Icon.plus(16)}
        </span>
        <span className="vxw-menu-name">Create a server</span>
      </button>
    </div>
  );
}

// ---------- hero action buttons ----------
export function ActionButton({ icon, title, sub, onClick, disabled }) {
  return (
    <button type="button" className="vxw-action" onClick={onClick} disabled={disabled}>
      <span className="vxw-action-icon">{icon}</span>
      <span className="vxw-action-text">
        <span className="vxw-action-title">{title}</span>
        <span className="vxw-action-sub">{sub}</span>
      </span>
    </button>
  );
}

// ---------- public servers ----------
// A server's own uploaded banner when the listing has one; otherwise a colour that stays the same for that server
// (derived from its key), never stock artwork.
export function ServerCard({ server, busy, note, onJoin, onOpen }) {
  let action;
  if (server.is_member && server.pending) {
    action = (
      <span className="vxw-join is-wait" role="status">
        Pending
      </span>
    );
  } else if (server.is_member) {
    action = (
      <button type="button" className="vxw-join" onClick={() => onOpen(server)}>
        Open
      </button>
    );
  } else {
    action = (
      <button type="button" className="vxw-join" onClick={() => onJoin(server)} disabled={busy} aria-label={`Join ${server.name}`}>
        {busy ? 'Joining…' : 'Join'}
      </button>
    );
  }
  return (
    <article className="vxw-scard" aria-label={server.name} title={server.summary || undefined}>
      <div className="vxw-scard-banner" aria-hidden="true">
        <ServerBannerArt seed={server.key || server.name} banner={server.banner} />
      </div>
      <div className="vxw-scard-body">
        <div className="vxw-scard-id">
          <ServerAvatar name={server.name} src={server.icon} size={34} className="is-round vxw-scard-avatar" />
          <h3 className="vxw-scard-name">
            <span>{server.name}</span>
            {server.encrypted ? (
              <span className="vxw-scard-lock" title="End-to-end encrypted" aria-label="End-to-end encrypted">
                {Icon.lock(13)}
              </span>
            ) : null}
          </h3>
        </div>
        <div className="vxw-scard-foot">
          <span className="vxw-scard-members">
            {Icon.users(17)}
            <span>{server.member_count_band} members</span>
          </span>
          {action}
        </div>
        {note ? (
          <p className={`vxw-scard-note ${note.type === 'error' ? 'is-error' : ''}`} role={note.type === 'error' ? 'alert' : 'status'}>
            {note.text}
          </p>
        ) : null}
      </div>
    </article>
  );
}

function ServerSkeleton() {
  return (
    <div className="vxw-scard vxw-skeleton" aria-hidden="true">
      <div className="vxw-scard-banner" />
      <div className="vxw-scard-body">
        <span className="vxw-sk vxw-sk-line" />
        <span className="vxw-sk vxw-sk-line short" />
      </div>
    </div>
  );
}

const PREVIEW_COUNT = 2;

export function PublicServers({ state, expanded, onToggleExpanded, onRetry, onJoin, onOpen, busyKey, notes, isSignedIn, onCreateServer, onEnterInvite, sectionRef }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const servers = useMemo(() => (state && Array.isArray(state.servers) ? state.servers : []), [state]);
  const categories = useMemo(() => Array.from(new Set(servers.map((s) => s.category).filter((c) => CATEGORY[c]))), [servers]);
  const filtered = useMemo(() => {
    if (!expanded) return servers.slice(0, PREVIEW_COUNT);
    const q = query.trim().toLowerCase();
    return servers.filter((s) => {
      if (category && s.category !== category) return false;
      if (!q) return true;
      return [s.name, s.summary, ...(s.tags || [])].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [servers, expanded, query, category]);

  const status = state ? state.status : 'loading';

  let list;
  if (status === 'loading') {
    list = (
      <div className="vxw-scards" aria-busy="true" aria-label="Loading public servers">
        <ServerSkeleton />
      </div>
    );
  } else if (status === 'error') {
    list = (
      <div className="vxw-public-msg" role="alert">
        <p>Public servers couldn’t be loaded.</p>
        <button type="button" className="vxw-join" onClick={onRetry}>
          {Icon.refresh(15)} Try again
        </button>
      </div>
    );
  } else if (!servers.length) {
    list = (
      <div className="vxw-public-msg">
        <p>No public servers are listed yet.</p>
        <button type="button" className="vxw-textlink" onClick={onEnterInvite}>
          Have an invite code?
        </button>
      </div>
    );
  } else if (!filtered.length) {
    list = (
      <div className="vxw-public-msg">
        <p>No servers match. Try another search or category.</p>
      </div>
    );
  } else {
    list = (
      <div className="vxw-scards">
        {filtered.map((s) => (
          <ServerCard key={s.key} server={s} busy={busyKey === s.key} note={notes[s.key]} onJoin={onJoin} onOpen={onOpen} />
        ))}
      </div>
    );
  }

  return (
    <section className="vxw-panel vxw-public" aria-labelledby="vxw-public-title" ref={sectionRef} tabIndex={-1}>
      <div className="vxw-panel-head">
        <span className="vxw-panel-icon">{Icon.people(28)}</span>
        <h2 id="vxw-public-title">Public servers to join</h2>
        {servers.length > PREVIEW_COUNT ? (
          <button type="button" className="vxw-textlink vxw-panel-link" onClick={onToggleExpanded} aria-expanded={expanded}>
            {expanded ? 'Show fewer' : `View all (${servers.length})`}
          </button>
        ) : null}
      </div>

      {expanded && servers.length ? (
        <div className="vxw-filter">
          <label className="vxw-search">
            {Icon.search(17)}
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search servers, topics or tags" aria-label="Search public servers" />
          </label>
          {categories.length > 1 ? (
            <div className="vxw-cats" role="group" aria-label="Filter by category">
              <button type="button" className={`vxw-cat ${!category ? 'is-on' : ''}`} aria-pressed={!category} onClick={() => setCategory('')}>
                All
              </button>
              {categories.map((c) => (
                <button key={c} type="button" className={`vxw-cat ${category === c ? 'is-on' : ''}`} aria-pressed={category === c} onClick={() => setCategory(c)}>
                  {categoryLabel(c)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={`vxw-public-body ${expanded ? 'is-expanded' : ''}`}>
        <div className="vxw-public-list">{list}</div>
        <div className="vxw-public-divider" aria-hidden="true" />
        <div className="vxw-public-cta">
          <p className="vxw-public-cta-text">More communities coming soon — create your own.</p>
          <button type="button" className="vxw-create" onClick={onCreateServer} title={isSignedIn ? undefined : 'Log in to create a server'}>
            {Icon.plus(20)}
            <span>Create a Server</span>
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------- continue where you left off ----------
function Tile({ icon, title, sub, onClick, disabled }) {
  return (
    <button type="button" className="vxw-tile" onClick={onClick} disabled={disabled}>
      <span className="vxw-tile-icon">{icon}</span>
      <span className="vxw-tile-text">
        <span className="vxw-tile-title">{title}</span>
        <span className="vxw-tile-sub">{sub}</span>
      </span>
    </button>
  );
}

export function ContinuePanel({ isSignedIn, recent, myServers, onArea, onOpenServer, onOpenDm, onResume, onSignIn, onCreateServer, onBrowse, onClearRecent }) {
  const servers = myServers && Array.isArray(myServers.servers) ? myServers.servers : [];
  const loaded = myServers && myServers.status === 'ok';
  const noServers = isSignedIn && loaded && !servers.length;
  const needsServer = (fn) => (noServers ? undefined : fn);

  const resume = recent && recent.resume;
  const dms = (recent && recent.recentDms) || [];
  const dmTotal = (recent && recent.dmTotal) || 0;
  const unread = dms.some((d) => d.unread);

  let serversSub = 'Find a community';
  if (isSignedIn && resume) serversSub = `Continue in ${resume.serverName}`;
  else if (isSignedIn && loaded && servers.length) serversSub = `Continue in ${servers[0].name}`;

  return (
    <section className="vxw-panel vxw-continue" aria-labelledby="vxw-continue-title">
      <div className="vxw-panel-head">
        <span className="vxw-panel-icon">{Icon.clock(26)}</span>
        <h2 id="vxw-continue-title">Continue where you left off</h2>
        {isSignedIn && dms.length ? (
          <div className="vxw-recently">
            <span className="vxw-recently-label">Recently active</span>
            <button type="button" className="vxw-textlink is-underlined" onClick={onClearRecent}>
              Clear
            </button>
            <div className="vxw-stack">
              {dms.map((d) => (
                <button key={d.id} type="button" className="vxw-stack-item" onClick={() => onOpenDm(d)} title={`${d.name}${d.unread ? ' (new message)' : ''}`} aria-label={`Open your conversation with ${d.name}`}>
                  <ServerAvatar name={d.name} src={d.avatar} size={28} className="is-round" />
                  {d.unread ? <span className="vxw-dot" /> : null}
                </button>
              ))}
              {dmTotal > dms.length ? (
                <button type="button" className="vxw-stack-more" onClick={() => onArea('social', { socialSection: 'dms' })} aria-label={`${dmTotal - dms.length} more conversations`}>
                  +{dmTotal - dms.length}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="vxw-tiles">
        <Tile
          icon={Icon.play(32)}
          title="Watch Together"
          sub="Synced playback with friends"
          onClick={isSignedIn ? needsServer(() => onArea('watch')) : onSignIn}
          disabled={noServers}
        />
        <Tile
          icon={Icon.chat(32)}
          title="Direct Messages"
          sub={unread ? 'New messages waiting' : 'Stay in touch'}
          onClick={isSignedIn ? needsServer(() => onArea('social', { socialSection: 'dms' })) : onSignIn}
          disabled={noServers}
        />
        <Tile icon={Icon.servers(32)} title="Servers" sub={serversSub} onClick={!isSignedIn ? onBrowse : resume ? () => onResume(resume) : servers.length ? () => onOpenServer(servers[0]) : onBrowse} />
      </div>

      {noServers ? (
        <p className="vxw-note" role="status">
          Watch Together and Direct Messages open inside a server. Join a public server above, use an invite code, or{' '}
          <button type="button" className="vxw-link" onClick={onCreateServer}>
            create your own
          </button>
          .
        </p>
      ) : null}
    </section>
  );
}
